import {useMemo} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem, DeliveryMerchantChartData} from '@/map/@types';
import {useDeliveryData} from '@/map/api';
import {DEVIVERY_COMPANIES} from '@/map/components/AnalysisLayer/AnalysisPanel/Delivery/constants';
import {DeliveryServiceType} from '@/map/enums';
import {ColumnFlexBox, Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber, withComma} from '@/utils';
import {ErrorBox} from './ErrorBox';
import {NoData} from './NoData';
import {
  ColorCircle, ReportContainer, SectionLabel, SectionValue, Table, TableCell, TableHeaderRow,
  TableRow
} from './StyledComponents';

interface IProps {
  item: AnalysisItem;
}

const getPercent = (number?: number, total?: number) => {
  if (!number || !total) {
    return '-';
  }

  return `${Math.round((number / total) * 100)}%`;
};

export const DeliveryReport = ({item}: IProps) => {
  const {data: raw, isPending, error} = useDeliveryData(item);

  const data = useMemo<DeliveryMerchantChartData[] | undefined>(() => {
    if (raw?.merchants) {
      const merchants = raw.merchants;

      return Object.keys(merchants)
        .filter((key) => (merchants[key].transactionCount ?? 0) > 0)
        .map((name) => ({
          name,
          ...DEVIVERY_COMPANIES[name as DeliveryServiceType],
          ...raw.merchants?.[name],
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));
    } else {
      return [];
    }
  }, [raw]);

  if (isPending || error || !data?.length) {
    return (
      <ReportContainer>
        {isPending ? (
          <LoadingBar color={palette.textSecondary} />
        ) : error ? (
          <ErrorBox />
        ) : (
          <NoData text="분석 지역 내 배달 매출 정보가 없습니다." />
        )}
      </ReportContainer>
    );
  }

  return (
    <ReportContainer>
      <ColumnFlexBox gap={40}>
        <div>
          <Flexbox>
            <SectionLabel>총 배달 매출</SectionLabel>
            <FlexFullGrow />
            <SectionValue>
              {humanizeNumber(raw?.total?.transactionSum, true)} 원
            </SectionValue>
          </Flexbox>
          <PaddingBox top={16}>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableCell>분류</TableCell>
                  <TableCell>매출액(비중)</TableCell>
                </TableHeaderRow>
              </thead>
              <tbody>
                {data.map((i) => (
                  <TableRow key={i.name}>
                    <TableCell>
                      <Flexbox>
                        <ColorDot color={i.color} />
                        <div>{i.label}</div>
                      </Flexbox>
                    </TableCell>
                    <TableCell>
                      {humanizeNumber(i.transactionSum, true)} 원 (
                      {getPercent(i.transactionSum, raw?.total?.transactionSum)}
                      )
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </PaddingBox>
        </div>
        <div>
          <Flexbox>
            <SectionLabel>총 배달 건수</SectionLabel>
            <FlexFullGrow />
            <SectionValue>
              {withComma(raw?.total?.transactionCount)} 건
            </SectionValue>
          </Flexbox>
          <PaddingBox top={16}>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableCell>분류</TableCell>
                  <TableCell>주문건수</TableCell>
                </TableHeaderRow>
              </thead>
              <tbody>
                {data.map((i) => (
                  <TableRow key={i.name}>
                    <TableCell>
                      <Flexbox>
                        <ColorDot color={i.color} />
                        <div>{i.label}</div>
                      </Flexbox>
                    </TableCell>
                    <TableCell>{withComma(i.transactionCount)} 건</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </PaddingBox>
        </div>
        <div>
          <SectionLabel>객단가</SectionLabel>
          <PaddingBox top={16}>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableCell>분류</TableCell>
                  <TableCell>객단가</TableCell>
                </TableHeaderRow>
              </thead>
              <tbody>
                {data.map((i) => (
                  <TableRow key={i.name}>
                    <TableCell>
                      <Flexbox>
                        <ColorDot color={i.color} />
                        <div>{i.label}</div>
                      </Flexbox>
                    </TableCell>
                    <TableCell>
                      {withComma(
                        Math.round(
                          (i.transactionSum ?? 0) / (i.transactionCount ?? 0)
                        )
                      )}
                      원
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </PaddingBox>
        </div>
      </ColumnFlexBox>
    </ReportContainer>
  );
};

const ColorDot = styled(ColorCircle)`
  margin-right: 20px;
`;
